import { useCliftContext } from "../../hooks/useCliftContext";
import { CliftReducerAction } from "../../context/clift-context/clift-action-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TenantForm } from "./TenantForm";
import { createTenant } from "../../services/tenants-api";
import SideModal from "../../components/SideModal";
import { TenantAdmin, TenantFormValues, TenantType } from "../../models/tenant";
import {
  getErrorCode,
  getLocalizedErrorReason,
} from "../../services/clift-api-errors";
import { Contact } from "../../models/contact";
import { SideModalButtonBarProps } from "../../components/SideModalButtonBar";
import { toast } from "react-toast";

export const CreateTenant = () => {
  const { cliftState, dispatchCliftState } = useCliftContext();
  const [loading, setLoading] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const { t } = useTranslation();
  const [bottomButtonsProps, setBottomButtonsProps] =
    useState<SideModalButtonBarProps>();

  if (loading) return <progress></progress>;

  const closeSidePanel = () => {
    dispatchCliftState({
      type: CliftReducerAction.CloseSidePanel,
    });
  };

  const onCreateTenant = (
    tenant: TenantFormValues,
    admins: TenantAdmin[],
    contacts: Contact[]
  ) => {
    if (!cliftState.currentTenant) throw new Error("No current tenant");

    setLoading(true);

    const parentTenantId = cliftState.currentTenant.id;
    const tenantType =
      cliftState.currentTenant.tenantType === TenantType.ROOT
        ? TenantType.COUNTRY
        : TenantType.DEALER;

    const tenantData = {
      ...tenant,
      admins,
      contacts,
    };

    return createTenant(tenantData, parentTenantId, tenantType)
      .then(() => {
        setLoading(false);
        dispatchCliftState({
          type: CliftReducerAction.FetchTenants,
        });
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert:
            cliftState.currentTenant?.tenantType === TenantType.ROOT
              ? t("country_add_success", {
                  ns: "alerts",
                })
              : t("lift_provider_add_success", {
                  ns: "alerts",
                }),
        });
        toast.success(
          cliftState.currentTenant?.tenantType === TenantType.ROOT
            ? t("add_country_success", { ns: "toasts" })
            : t("add_lift_provider_success", { ns: "toasts" })
        );
      })
      .catch((err) => {
        setLoading(false);
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert:
            cliftState.currentTenant?.tenantType === TenantType.ROOT
              ? t("country_add_failed", {
                  ns: "alerts",
                  code: getErrorCode(err),
                })
              : t("lift_provider_add_failed", {
                  ns: "alerts",
                  code: getErrorCode(err),
                }),
        });
        toast.error(
          t("error") + " " + cliftState.currentTenant?.tenantType ===
            TenantType.ROOT
            ? t("add_country_failed", {
                ns: "toasts",
                reason: getLocalizedErrorReason(err),
              })
            : t("add_lift_provider_failed", {
                ns: "toasts",
                reason: getLocalizedErrorReason(err),
              })
        );
      });
  };

  return (
    <SideModal
      title={
        cliftState.currentTenant?.tenantType === TenantType.ROOT
          ? t("create_market_tenant", { ns: "tenant" })
          : t("create_lift_provider", { ns: "tenant" })
      }
      showPager
      pageCount={3}
      pageIndex={currentPageIndex}
      bottomButtonsProps={bottomButtonsProps}
      onClose={closeSidePanel}
    >
      <TenantForm
        pageIndex={currentPageIndex}
        onSubmit={onCreateTenant}
        onClose={closeSidePanel}
        onSwitchPage={setCurrentPageIndex}
        setBottomButtonsProps={setBottomButtonsProps}
      />
    </SideModal>
  );
};
